import { Option, SelectSearch } from '@/components/commons/select-search';
import CartIcon from '@/components/icons/cart-icon';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import SignInIcon from '@/components/icons/signin-icon';
import CardShippingCheckout from '@/components/sections/builder/elements/body/checkout-form/component/card-shipping-checkout';
import { CheckoutItem } from '@/components/sections/builder/elements/body/checkout-form/component/checkout-item';
import ModalSelectAddress from '@/components/sections/builder/elements/body/checkout-form/component/modal-select-address';
import { CheckoutFormElemetDataType } from '@/components/sections/builder/elements/body/checkout-form/type';
import ModalAlert from '@/components/sections/modal-alert';
import CardPaymentMethod from '@/components/sections/order/form/card-payment-method';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { FormControl, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { useAreas } from '@/hooks/api/storefront/orders';
import { useDebounceValue } from '@/hooks/use-debounce';
import useModal from '@/hooks/useModal';
import {
  createExternalOrder,
  updateGetShippingRates,
  updatePickupCart,
  updateShippingInfo,
} from '@/lib/actions/checkout/action';
import { clearAuthCookies } from '@/lib/actions/customer/action';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { queryClient } from '@/lib/query-client';
import { cn, isEmpty, normalizePinpoint } from '@/lib/utils';
import { useCustomerStore } from '@/stores/customer';
import { HttpTypes } from '@/types';
import { IAreas, IShippingPromotion, IShippingShipperPromotion } from '@/types/area';
import { BaseCart } from '@/types/http/cart/common';
import { BaseCustomerAddress, BaseCustomerDetail } from '@/types/http/customer/common';
import {
  BaseShippingRatesPayload,
  PromotionCheckpoint,
  TExternalOrder,
} from '@/types/http/orders/common';
import { Form, Formik } from 'formik';
import { Calendar, CircleCheck, MailIcon, MapPinIcon } from 'lucide-react';
import Link from 'next/link';
import { useParams, usePathname, useRouter } from 'next/navigation';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { shallow } from 'zustand/shallow';
import Cookies from 'js-cookie';
import { useCookies } from '@/app/cookie-provider';
import { AxiosError } from 'axios';
import { MapResult } from '@/components/maps-service';
import MapService from '@/components/maps-service';
import { IStoreInfoData } from '@/types/general';
import { ThemeCartType, ThemePromotionType, ThemeType } from '@/stores/builder';
import { useMutateForcePromotion } from '@/hooks/api/storefront/promotion';
import DropshipperForm, {
  DropshipperFormData,
} from '@/components/sections/builder/elements/body/checkout-form/component/dropshipper-form';
import PickupSchedule from '@/components/sections/builder/elements/body/checkout-form/component/pickup-schedule';
import PickupAddress from '@/components/sections/builder/elements/body/checkout-form/component/pickup-address';
import { format } from 'date-fns';
import { MultiOriginLocation } from '@/types/http';

const CheckoutSchema = Yup.object().shape({});

export type CustomerAddressType = {
  area: string;
  area_name: string;
  address: string;
  zipcode: string;
  pinpoint: {
    lat: string | number;
    long: string | number;
  };
};

export type FormSelectShippingType = {
  shippingMethodId: string;
  shippingServiceId: string;
};

export type ShippingMethodCheckoutType = {
  id?: string;
  name?: string;
  courir_category_id?: string;
  courier_code: string;
  courier_logo: string;
  service?: {
    id?: string;
    name?: string;
    label?: string;
    price?: number;
    shipping_insurance?: number;
    is_include_insurance?: boolean;
    is_cod_supported?: boolean;
    cod_fee?: string;
  };
  promotion?: IShippingPromotion;
  shipper_promotion?: IShippingShipperPromotion;
  shipper_promotion_value?: number;
  shipper_discount_rates?: number;
  service_category_id?: number;
};

type CheckoutFormProps = {
  host: string;
  content: CheckoutFormElemetDataType;
  carts: HttpTypes.StoreCartDetail;
  formValues: TExternalOrder;
  paymentMethod: string;
  areaData: IAreas;
  address: CustomerAddressType;
  pinpoint: { lat: string; long: string };
  setPinpoint: React.Dispatch<React.SetStateAction<{ lat: string; long: string }>>;
  listAddress: BaseCustomerAddress[];
  dataShipping?: BaseShippingRatesPayload;
  isPreview?: boolean;
  isLoadingSelectShipping?: boolean;
  isInsurance?: boolean;
  dataProfile?: BaseCustomerDetail;
  setAreaData: (data: IAreas) => void;
  setIsLoadingSelectShipping: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentMethod: React.Dispatch<React.SetStateAction<string>>;
  setAddress: React.Dispatch<React.SetStateAction<CustomerAddressType>>;
  setIsInsurance: React.Dispatch<React.SetStateAction<boolean>>;
  handleChangeAddress: (address: BaseCustomerAddress) => void;
  storeSetting?: IStoreInfoData;
  promotionTheme?: ThemePromotionType;
  cartTheme?: ThemeCartType;
  selectedOption?: 'delivery' | 'pickup_in_store';
  setSelectedOption?: React.Dispatch<React.SetStateAction<'delivery' | 'pickup_in_store'>>;
  theme?: ThemeType;
};

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  host,
  content,
  carts,
  paymentMethod,
  address,
  pinpoint,
  setPinpoint,
  formValues,
  areaData,
  listAddress,
  dataShipping,
  isPreview,
  isLoadingSelectShipping,
  isInsurance,
  dataProfile,
  setAreaData,
  setIsLoadingSelectShipping,
  setAddress,
  setIsInsurance,
  setPaymentMethod,
  handleChangeAddress,
  storeSetting,
  promotionTheme,
  cartTheme,
  selectedOption,
  setSelectedOption,
  theme,
}) => {
  const { checkoutId } = useParams();
  const { cartId } = useCookies();
  const token = Cookies.get('accessToken') as string;
  const modalSelectAddress = useModal();
  const modalCODPayment = useModal();
  const modalForcePromotion = useModal<{ email: string }>();
  const pathname = usePathname();
  const router = useRouter();

  const { setCustomer, customer, clearCustomer } = useCustomerStore(
    (state) => ({
      setCustomer: state.setCustomer,
      customer: state.customer,
      clearCustomer: state.clearCustomer,
    }),
    shallow
  );

  const [searchValue, setSearchValue] = React.useState<string>('');
  const [isCODAvailable, setIsCODAvailable] = React.useState<boolean>(false);
  const [selectedShipping, setSelectedShipping] = React.useState<ShippingMethodCheckoutType>();
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [isCOD, setIsCOD] = React.useState<boolean>(false);
  const [hash, setHash] = React.useState<string>('');
  const [isShowMap, setIsShowMap] = React.useState<boolean>(false);
  const [listPromoError, setListPromoError] = React.useState<
    {
      message: string;
      promotion_name: string;
      promotion_id: string;
    }[]
  >([]);

  const [dataDropshipper, setDataDropshipper] = React.useState<DropshipperFormData>({
    isDropshipper: false,
    name: formValues?.customer?.first_name || '',
    lastName: formValues?.customer?.last_name || '',
    phoneNumber: formValues?.customer?.phone || '',
  });

  const [selectedLocationPickup, setSelectedLocationPickup] = useState<
    HttpTypes.StoreOrderLocationPickup | MultiOriginLocation
  >();
  const [datePickup, setDatePickup] = useState<Date>();

  const { mutateAsync: mutateForcePromotion, isPending: isPendingForcePromotion } =
    useMutateForcePromotion({
      onSuccess: async (data) => {
        if (data.statusCode === 200) {
          setListPromoError([]);

          await queryClient.invalidateQueries({
            queryKey: ['checkoutDetail', checkoutId],
            refetchType: 'all',
          });

          modalForcePromotion.close();
        }
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          toast({
            title: 'Error',
            description: error.response?.data.message || 'Something went wrong',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Error',
            description: 'Something went wrong',
            variant: 'destructive',
          });
        }
      },
    });

  const debouncedSearchValue = useDebounceValue(searchValue, 500);

  const { data: areas } = useAreas(
    {
      q: debouncedSearchValue,
    },
    {
      enabled: !!debouncedSearchValue,
    }
  );

  const areaOptions =
    areas?.map((item) => ({
      label: item.name,
      value: item.area_id,
    })) || [];

  const handleInputChange = React.useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleSelectArea = async (value: Option) => {
    try {
      setIsLoadingSelectShipping(true);

      const area = areas?.find((item) => item.area_id === value.value);
      const res = await updateShippingInfo(host, checkoutId as string, {
        destination: {
          area_id: area?.area_id,
          zipcode: area?.zipcode,
          coordinate:
            pinpoint && pinpoint.lat && pinpoint.long ? `(${pinpoint.lat}, ${pinpoint.long})` : '',
        },
      });

      if (res.success) {
        setHash(res.data?.hash as string);
      }

      queryClient.invalidateQueries({ queryKey: ['checkoutDetail', checkoutId] });
      setAreaData(area as IAreas);
      setSelectedShipping(undefined);
      setPaymentMethod('online');
      setIsCOD(false);
    } catch (error) {
      let errorMessage = 'Something went wrong';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingSelectShipping(false);
    }
  };

  const handleSelectPinpoint = async (result: MapResult) => {
    try {
      setIsLoadingSelectShipping(true);
      setIsCODAvailable(false);

      const { lat, lng } = result.coordinates;
      const res = await updateShippingInfo(host, checkoutId as string, {
        destination: {
          area_id: areaData.area_id,
          zipcode: areaData.zipcode,
          coordinate: lat && lng ? `(${lat}, ${lng})` : '',
        },
      });

      if (res.success) {
        setHash(res.data?.hash as string);
      }

      setPinpoint({ lat: String(lat), long: String(lng) });
      setCustomer({
        ...customer,
        pinpoint: { lat: String(lat), lng: String(lng) },
      });

      queryClient.invalidateQueries({ queryKey: ['checkoutDetail', checkoutId] });
    } catch (error) {
      let errorMessage = 'Something went wrong';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingSelectShipping(false);
    }
  };

  const handleSubmitCheckout = async (values: TExternalOrder, isCOD: boolean) => {
    const items = carts?.items?.map((item: BaseCart) => ({
      item_id: item.id,
      qty: item.quantity,
      price: item.sell_price,
      discount: 0,
      tax: 0,
      total: item.total_value,
    }));

    const customerData = !dataProfile
      ? {
          first_name: dataDropshipper.isDropshipper
            ? dataDropshipper.name
            : values?.customer?.first_name,
          last_name: dataDropshipper.isDropshipper
            ? dataDropshipper.lastName
            : values?.customer?.last_name,
          phone: dataDropshipper.isDropshipper
            ? dataDropshipper.phoneNumber
            : values?.customer?.phone,
          email: values?.customer?.email,
          area: areaData.area_id || customer.area_id,
          address: values?.customer?.address,
          province: areaData.province,
          zipcode: areaData.zipcode,
          country: values?.customer?.country || 'indonesia',
          pinpoint: {
            lat: values?.customer?.pinpoint?.lat ? String(values?.customer?.pinpoint?.lat) : '',
            long: values?.customer?.pinpoint?.long ? String(values?.customer?.pinpoint?.long) : '',
          },
        }
      : undefined;

    const province = areaData?.area_id.substring(0, 2) || customer.area_id.substring(0, 2);
    const city = areaData?.area_id.substring(0, 4) || customer.area_id.substring(0, 4);
    const district = areaData?.area_id.substring(0, 6) || customer.area_id.substring(0, 6);

    const splitAreaName = areaData?.name.split(', ') || customer.area.split(', ');
    const areaInfo = {
      subdistrict: splitAreaName?.[0],
      city: splitAreaName?.[2],
      province: splitAreaName?.[splitAreaName?.length - 1]?.split(' ')?.[0],
      zipcode: splitAreaName?.[splitAreaName?.length - 1]?.split(' ')?.[1],
    };

    const shippingInfo = {
      shipping_province: values?.customer?.province || areaData?.province || areaInfo.province,
      shipping_province_id: province,
      shipping_city: areaData?.city || areaInfo.city,
      shipping_city_id: city,
      shipping_district_id: district,
      shipping_area: areaData?.area_id || customer.area_id,
      shipping_subdistrict_id: areaData?.area_id || customer.area_id,
      shipping_subdistrict: areaData?.name || areaInfo.subdistrict,
      shipping_post_code: values?.customer?.zipcode || areaData?.zipcode || areaInfo.zipcode,
      shipping_address: values?.customer?.address,
      shipping_service_id: selectedShipping?.service?.id as string,
      shipping_first_name: values?.customer?.first_name,
      shipping_last_name: values?.customer?.last_name,
      shipping_phone: values?.customer?.phone,
      shipping_coordinate: {
        lat: values?.customer?.pinpoint?.lat ? String(values?.customer?.pinpoint?.lat) : '',
        long: values?.customer?.pinpoint?.long ? String(values?.customer?.pinpoint?.long) : '',
      },
      dropshipper_first_name: dataDropshipper.isDropshipper ? dataDropshipper.name : '',
      dropshipper_last_name: dataDropshipper.isDropshipper ? dataDropshipper.lastName : '',
      dropshipper_phone: !dataProfile
        ? dataDropshipper.isDropshipper
          ? dataDropshipper.phoneNumber
          : ''
        : values?.customer?.phone,
      dropshipper_email: dataDropshipper.isDropshipper ? values?.customer?.email : '',
    };

    const payload = {
      cart_id: cartId,
      checkout_id: checkoutId,
      customer: customerData,
      shipping_info: shippingInfo,
    } as TExternalOrder;

    if (selectedOption === 'delivery') {
      Object.assign(payload, {
        is_include_insurance: isInsurance,
        shipping_hash: hash ?? carts?.shipping_hash,
        notes: values?.notes,
        is_dropship: dataDropshipper.isDropshipper,
      });
    }

    if (dataProfile && token) {
      delete payload.customer;
    }

    const result = await createExternalOrder(host as string, payload);

    if (result.success) {
      await fetch('/api/remove-cart-cookie', {
        method: 'GET',
      });

      toast({
        title: 'Success',
        description: 'Order has been successfully created',
        variant: 'success',
      });

      SendEventAnalytic('begin_checkout', {
        transaction_id: payload.cartId,
        currency: 'IDR',
        value: carts?.grand_total as number,
        tax: carts?.tax_amount as number,
        shipping: carts?.shipping_cost as number,
        items: items,
      });

      SendEventMetapixel('InitiateCheckout', {
        content_ids: carts.items?.map((item) => item.id) || [],
        contents: carts.items?.map((item) => ({
          id: item.id || '',
          quantity: item.quantity || 0,
        })),
        num_items: carts.items?.length,
        content_type: 'checkout',
        value: carts.grand_total,
        currency: 'IDR',
      });

      clearCustomer();

      if (isCOD) {
        modalCODPayment.close();
        window.location.href = `/checkout/order?orderId=${result.data?.order_id as string}`;
      } else {
        window.location.href = result.data?.payment_url as string;
      }
    } else {
      if (Array.isArray(result.data) && result.data.length > 0) {
        setListPromoError(result.data);
        modalForcePromotion.open({ email: values.customer?.email as string });
        return;
      }

      toast({
        title: 'Error',
        description: result.message,
        variant: 'destructive',
      });
    }
  };

  const handleSubmitCheckoutCOD = async () => {
    setIsSubmitted(true);

    try {
      await handleSubmitCheckout(modalCODPayment.data as TExternalOrder, true);
    } catch (error) {
      let errorMessage = 'Something went wrong';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast({
        title: 'Error!',
        description: errorMessage,
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleSavePickup = async (date: Date) => {
    const now = new Date();
    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();

    const minValidTime = new Date(
      now.getTime() + (storeSetting?.min_preparation_time ?? 0) * 60 * 1000
    );

    if (isToday && date < minValidTime) {
      toast({
        title: 'Invalid Time',
        description: 'Selected time is not available yet based on preparation time.',
        variant: 'warning',
      });
      setDatePickup(undefined);
      return false;
    }

    const result = await updatePickupCart(host, {
      checkout_id: checkoutId as string,
      location_id: selectedLocationPickup?.location_id as string,
      pickup_time: format(date, 'yyyy-MM-dd HH:mm'),
    });

    if (!result?.success) {
      toast({
        title: 'Error',
        description: result?.message || 'Failed to save pickup schedule',
        variant: 'destructive',
      });
      setDatePickup(undefined);
      return false;
    }

    return true;
  };

  const handleOnSubmit = async (values: TExternalOrder) => {
    const emailToValidate = dataProfile?.email || values.customer?.email;
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToValidate || '');

    if (!isValidEmail) {
      toast({
        title: 'Invalid Email Format',
        description: 'Please enter a valid email address before proceeding.',
        variant: 'warning',
      });
      return;
    }

    if (dataDropshipper.isDropshipper) {
      if (!dataDropshipper.name.trim()) {
        toast({
          title: 'Missing First Name',
          description: "Please enter the dropshipper's first name.",
          variant: 'destructive',
        });
        return;
      }

      if (!dataProfile) {
        if (!dataDropshipper.phoneNumber.trim()) {
          toast({
            title: 'Missing Phone Number',
            description: "Please enter the dropshipper's phone number.",
            variant: 'destructive',
          });
          return;
        }
      }
    }
    const isDigitalProduct = storeSetting?.is_digital_product;

    if (!isDigitalProduct) {
      if (selectedOption !== 'pickup_in_store' && !areaData?.area?.trim()) {
        toast({
          title: 'Incomplete Information',
          description: 'Please select an area before proceeding.',
          variant: 'warning',
        });
        return;
      }

      if (selectedOption !== 'pickup_in_store' && isEmpty(selectedShipping)) {
        toast({
          title: 'Incomplete Information',
          description: 'Please select a shipping option before proceeding.',
          variant: 'warning',
        });
        return;
      }
    }

    if (selectedOption === 'pickup_in_store') {
      if (isEmpty(selectedLocationPickup) || !datePickup) {
        toast({
          title: 'Incomplete Information',
          description: 'Please select a pickup location and time.',
          variant: 'warning',
        });
        return;
      }
    }

    const payload = {
      ...values,
      hash: hash ?? carts.shipping_hash,
      is_dropship: dataDropshipper.isDropshipper,
    };

    if (isCOD) {
      modalCODPayment.open(payload);
    } else {
      setIsSubmitted(true);

      try {
        if (selectedOption === 'pickup_in_store') {
          const savedPickup = await handleSavePickup(datePickup as Date);
          if (!savedPickup) {
            setIsSubmitted(false);
            return;
          }
        }

        await handleSubmitCheckout(payload, false);
      } catch (error) {
        let errorMessage = 'Something went wrong';
        if (error instanceof AxiosError) {
          errorMessage = error.response?.data?.message || errorMessage;
        }
        toast({
          title: 'Error!',
          description: errorMessage,
          variant: 'destructive',
          duration: 3000,
        });
      } finally {
        setIsSubmitted(false);
      }
    }
  };

  const handleForcePromotion = async () => {
    const { email } = modalForcePromotion.data || {};

    await mutateForcePromotion({
      checkoutId: checkoutId as string,
      email: email as string,
      token,
    });
  };

  const handleSignOut = React.useCallback(async () => {
    await clearAuthCookies();
    window.location.reload();
  }, []);

  const handleSelectShipping = async (data: ShippingMethodCheckoutType) => {
    setIsLoadingSelectShipping(true);

    try {
      setSelectedShipping(data);
      setPaymentMethod('online');
      setIsCOD(false);
      setIsCODAvailable(data?.service?.is_cod_supported as boolean);

      const payload: HttpTypes.StoreUpdateShippingRates = {
        checkout_id: checkoutId as string,
        service_category_id: String((data.courir_category_id as string) || ''),
        courier_id: Number((data.id as string) || 0),
        is_cod: false,
        is_include_insurance: false,
      };

      if (data.promotion?.promotion_id) {
        payload.promotion_id = data?.promotion?.promotion_id;
      }

      if (data.shipper_promotion?.promotion_id) {
        payload.shipper_promotion_id = data?.shipper_promotion?.promotion_id;
        payload.shipper_discount_rates = data?.shipper_discount_rates ?? 0;
        payload.shipper_promotion_value = data?.shipper_promotion?.promotion_value ?? 0;
      }

      const result = await updateGetShippingRates(host as string, payload);
      if (result.success) {
        setIsInsurance(false);
        queryClient.invalidateQueries({ queryKey: ['checkoutDetail', checkoutId] });

        SendEventAnalytic('add_shipping_info', {
          currency: 'IDR',
          value: dataShipping?.total_value as number,
          shipping_tier: data.courier_code,
          items: dataShipping?.items.map((item) => ({
            item_name: item.item_name as string,
            quantity: item.quantity as number,
            item_weight: item.weight as string,
          })),
        });

        SendEventMetapixel('AddShippingInfo', {
          content_ids: data.courier_code,
          content_type: 'checkout',
          content_name: data.service?.name,
          shipping: data?.service?.price,
          value: dataShipping?.total_value,
          currency: 'IDR',
        });
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Failed to get courier info',
          variant: 'destructive',
        });
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast({
          title: 'Error',
          description: error.response?.data.message || 'Something went wrong',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoadingSelectShipping(false);
    }
  };

  const handleOnCheckedInsurance = async (checked: boolean) => {
    setIsInsurance(checked);
  };

  const handleSelectPayment = async (data: string) => {
    setIsLoadingSelectShipping(true);
    setPaymentMethod(data);

    setIsCOD(data === 'cod' ? true : false);

    try {
      const payload = {
        checkout_id: String(checkoutId ?? ''),
        destination: {
          area_id: areaData.area_id,
          zipcode: areaData.zipcode,
          coordinate:
            pinpoint?.lat && pinpoint?.long ? `(${pinpoint.lat}, ${pinpoint.long})` : undefined,
        },
        service_category_id: String(selectedShipping?.courir_category_id ?? ''),
        courier_id: Number(selectedShipping?.id ?? 0),
        is_cod: data === 'cod',
        is_include_insurance: isInsurance,
        ...(selectedShipping?.promotion?.promotion_id && {
          promotion_id: selectedShipping.promotion.promotion_id,
        }),
        ...(selectedShipping?.shipper_promotion?.promotion_id && {
          shipper_promotion_id: selectedShipping.shipper_promotion.promotion_id,
          shipper_promotion_value: selectedShipping.shipper_promotion.promotion_value,
          shipper_discount_rates: selectedShipping.shipper_discount_rates,
        }),
      };

      const result = await updateGetShippingRates(host as string, payload);

      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['checkoutDetail', checkoutId] });
      }
    } catch (error) {
      let errorMessage = 'Something went wrong';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingSelectShipping(false);
    }
  };

  const handleSelectedOption = async () => {
    setIsCOD(false);
    setSelectedOption?.('pickup_in_store');
  };

  const disableButtonPay = () => {
    if ((isPreview as boolean) || storeSetting?.is_digital_product) {
      return false;
    } else if (
      (selectedOption === 'delivery' &&
        (isSubmitted || isEmpty(selectedShipping) || !areaData?.area)) ||
      (selectedOption === 'pickup_in_store' && (!datePickup || isEmpty(selectedLocationPickup))) ||
      isPendingForcePromotion ||
      isLoadingSelectShipping
    ) {
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    if (pinpoint) {
      setAddress({
        ...address,
        pinpoint: {
          lat: pinpoint.lat,
          long: pinpoint.long,
        },
      });
    }
  }, [pinpoint]);

  React.useEffect(() => {
    const updateShippingRates = async () => {
      const res = await updateShippingInfo(host, checkoutId as string, {
        destination: {
          area_id: formValues.customer?.area,
          zipcode: formValues.customer?.zipcode,
          coordinate:
            pinpoint && pinpoint.lat && pinpoint.long ? `(${pinpoint.lat}, ${pinpoint.long})` : '',
        },
      });

      if (res.success) {
        setHash(res.data?.hash as string);
      }

      queryClient.invalidateQueries({ queryKey: ['checkoutDetail', checkoutId] });
    };

    if (formValues && formValues.customer) {
      updateShippingRates();
    }
  }, [formValues.customer]);

  React.useEffect(() => {
    if (
      dataProfile &&
      dataDropshipper.isDropshipper &&
      (!dataDropshipper.name || !dataDropshipper.lastName)
    ) {
      setDataDropshipper((prev) => ({
        ...prev,
        name: dataProfile.first_name || '',
        lastName: dataProfile.last_name || '',
      }));
    }
  }, [dataProfile, dataDropshipper.isDropshipper]);

  const grandTotal = carts?.grand_total ?? 0;
  const [checkpoints, setCheckpoints] = React.useState<PromotionCheckpoint[]>([]);

  return (
    <>
      <div className='bg_checkout_form w-full px-6 py-5 md:w-[55%] md:px-9'>
        <Formik
          key={JSON.stringify(formValues)}
          initialValues={formValues}
          validationSchema={CheckoutSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values, errors, handleSubmit, handleChange, handleBlur, touched, setFieldValue }) => (
            <Form
              className='space-y-4'
              onSubmit={handleSubmit}
              style={
                {
                  '--text-color': content?.colorSchemeForm?.text || '#000',
                  '--background': content?.colorSchemeForm?.background || '#fff',
                  '--btn-background': content?.colorSchemeForm?.btnSolidBackgroud || '#fff',
                  '--btn-label': content?.colorSchemeForm?.btnSolidlabel || '#000',
                } as React.CSSProperties
              }
            >
              <div className='flex w-full flex-col gap-4 pb-2 md:flex-row md:gap-6'>
                <Link href='/' className='w-full text-base'>
                  <Button
                    type='button'
                    variant='outline'
                    className={cn('flex w-full items-center gap-x-2 py-6')}
                  >
                    <CartIcon />
                    {content?.labelContinueShopping || 'Continue shopping'}
                  </Button>
                </Link>
                {!dataProfile && (
                  <Link
                    href={{
                      pathname: '/account/signin',
                      query: `checkout_url=${encodeURIComponent(pathname)}`,
                    }}
                    className='w-full text-base'
                  >
                    <Button
                      type='button'
                      variant='outline'
                      className={cn('flex w-full items-center gap-x-2 py-6')}
                      style={
                        !isEmpty(content.colorSchemeForm)
                          ? {
                              color: content.colorSchemeForm?.btnSolidBackgroud,
                              borderColor: content.colorSchemeForm?.btnSolidBackgroud,
                            }
                          : {}
                      }
                    >
                      <SignInIcon
                        color={
                          !isEmpty(content.colorSchemeForm)
                            ? content.colorSchemeForm?.btnSolidBackgroud
                            : undefined
                        }
                      />
                      Login
                    </Button>
                  </Link>
                )}
              </div>

              {(storeSetting?.is_pickup_in_store || isPreview) && (
                <div className='mb-4 flex w-full items-center gap-2'>
                  <Button
                    type='button'
                    variant={null}
                    onClick={() => setSelectedOption?.('delivery')}
                    className={cn(
                      'inline-flex w-full gap-3 rounded-full border p-2 text-center transition-all duration-200',
                      selectedOption === 'delivery'
                        ? 'border-[--btn-background] text-[--btn-background]'
                        : 'border-gray-300 !text-[--text-color] text-gray-700 hover:border-[--btn-background]'
                    )}
                  >
                    <span className='w-full text-sm font-medium'>Delivery</span>
                  </Button>

                  <Button
                    type='button'
                    variant={null}
                    onClick={handleSelectedOption}
                    className={cn(
                      'inline-flex w-full gap-3 rounded-full border p-2 text-center transition-all duration-200',
                      selectedOption === 'pickup_in_store'
                        ? 'border-[--btn-background] text-[--btn-background]'
                        : 'border-gray-300 !text-[--text-color] text-gray-700 hover:border-[--btn-background]'
                    )}
                  >
                    <span className='w-full text-sm font-medium'>Pickup in Store</span>
                  </Button>
                </div>
              )}

              {selectedOption === 'delivery' ? (
                <>
                  <div className='border-b pb-6'>
                    {dataProfile ? (
                      <Accordion type='single' collapsible className='w-full'>
                        <AccordionItem value='account-1'>
                          <AccordionTrigger
                            className={cn(
                              'py-1',
                              isEmpty(content.colorSchemeForm) ? 'button_color_scheme' : ''
                            )}
                            style={
                              !isEmpty(content.colorSchemeForm)
                                ? {
                                    color: content.colorSchemeForm?.btnSolidBackgroud,
                                  }
                                : {}
                            }
                          >
                            Account
                          </AccordionTrigger>
                          <span className='-mt-1 text-sm'>{dataProfile?.email}</span>
                          <AccordionContent>
                            <span
                              className='button_color_scheme text-sm font-normal underline hover:cursor-pointer'
                              onClick={handleSignOut}
                            >
                              Sign out
                            </span>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : (
                      <div className='mb-3 flex items-center'>
                        <div className='flex items-center gap-x-2'>
                          <MailIcon className='mt-0.5 h-5 w-5' />
                          <h2 className='text-lg md:text-xl'>Contact</h2>
                        </div>
                      </div>
                    )}
                    <FormItem>
                      {!dataProfile && (
                        <FormControl>
                          <div className='w-full flex-col'>
                            <Label
                              htmlFor='customer.email'
                              className='mb-1 font-normal'
                              aria-required
                            >
                              Email address
                            </Label>
                            <Input
                              id='customer.email'
                              name='customer.email'
                              type='email'
                              value={values.customer?.email}
                              onChange={(e) => {
                                handleChange(e);
                                setCustomer({ ...customer, email: e.target.value });
                              }}
                              onBlur={handleBlur}
                              placeholder='Email'
                              required
                              readOnly={dataProfile ? true : false}
                            />
                            {errors.customer && touched.customer && (
                              <p className='mt-2 text-red-500'>{errors.customer}</p>
                            )}
                          </div>
                        </FormControl>
                      )}
                    </FormItem>
                  </div>

                  {storeSetting?.is_dropship && (
                    <div className='flex w-full flex-col gap-4 md:flex-row md:gap-6'>
                      <div className='w-full'>
                        <DropshipperForm
                          dataDropshipper={dataDropshipper}
                          setDataDropshipper={(value) => {
                            setDataDropshipper(value);
                            const newData =
                              typeof value === 'function' ? value(dataDropshipper) : value;
                            if (!dataProfile && newData.isDropshipper && !customer.phone) {
                              setFieldValue('customer.phone', newData.phoneNumber);
                            }
                          }}
                          showPhoneNumber={!dataProfile}
                        />
                      </div>
                    </div>
                  )}

                  <FormItem>
                    <div className='border-b pb-6'>
                      <div className='flex items-center justify-between pb-3 pt-2'>
                        <div className='flex items-center gap-x-2'>
                          <MapPinIcon className='mt-0.5 h-5 w-5' />
                          <h2 className='tmd:text-xl text-lg'>Delivery Address</h2>
                        </div>
                        {dataProfile && (
                          <Button
                            type='button'
                            variant='link'
                            className='button_color_scheme p-0 text-sm font-normal underline underline-offset-1'
                            onClick={() => modalSelectAddress.open()}
                          >
                            Change Address
                          </Button>
                        )}
                      </div>

                      <div className='w-full space-y-4'>
                        <div className='flex w-full flex-col items-center gap-4 md:flex-row'>
                          <FormControl>
                            <div className='w-full flex-col'>
                              <Label
                                htmlFor='customer.first_name'
                                className='mb-1 font-normal'
                                aria-required
                              >
                                First name
                              </Label>
                              <Input
                                id='customer.first_name'
                                name='customer.first_name'
                                value={values.customer?.first_name}
                                onChange={(e) => {
                                  handleChange(e);
                                  setCustomer({
                                    ...customer,
                                    first_name: e.target.value,
                                  });
                                }}
                                onBlur={handleBlur}
                                placeholder='First name'
                                required
                              />
                            </div>
                          </FormControl>
                          <FormControl>
                            <div className='w-full flex-col'>
                              <Label htmlFor='customer.last_name' className='mb-1 font-normal'>
                                Last name
                              </Label>
                              <Input
                                id='customer.last_name'
                                name='customer.last_name'
                                value={values.customer?.last_name}
                                className='w-full'
                                classNameWrapper='w-full'
                                onChange={(e) => {
                                  handleChange(e);
                                  setCustomer({
                                    ...customer,
                                    last_name: e.target.value,
                                  });
                                }}
                                onBlur={handleBlur}
                                placeholder='Last name'
                              />
                            </div>
                          </FormControl>
                        </div>
                        <FormControl>
                          <div className='flex-col'>
                            <Label
                              htmlFor='customer.phone'
                              className='mb-1 font-normal'
                              aria-required
                            >
                              Phone number
                            </Label>
                            <Input
                              id='customer.phone'
                              name='customer.phone'
                              value={values.customer?.phone}
                              onChange={(e) => {
                                handleChange(e);
                                setCustomer({
                                  ...customer,
                                  phone: e.target.value,
                                });
                              }}
                              onBlur={handleBlur}
                              placeholder='Phone'
                              maxLength={20}
                              required
                            />
                          </div>
                        </FormControl>
                        <Label
                          htmlFor='customer.area_name'
                          className='!-mb-3 block font-normal'
                          aria-required
                        >
                          Province, city, district, zipcode
                        </Label>
                        <SelectSearch
                          options={areaOptions}
                          placeholder='Search province, city, district or zipcode'
                          emptyMessage='No Area Found'
                          isCommandInput
                          onSelect={handleSelectArea}
                          onInputChange={handleInputChange}
                          isLoading={false}
                          defaultInputValue={values.customer?.area_name || areaData?.name}
                          disabled={isLoadingSelectShipping}
                        />
                        <FormControl>
                          <div>
                            <Label htmlFor='customer.address' className='mb-1 font-normal'>
                              Detail address
                            </Label>
                            <Textarea
                              id='customer.address'
                              name='customer.address'
                              value={values.customer?.address}
                              onChange={(e) => {
                                handleChange(e);
                                setCustomer({
                                  ...customer,
                                  address: e.target.value,
                                });
                              }}
                              onBlur={handleBlur}
                              placeholder='Detail address'
                              required
                            />
                          </div>
                        </FormControl>
                        <div className='flex items-center justify-between'>
                          <div className='flex flex-col gap-2'>
                            <Label
                              htmlFor='customer.pinpoint'
                              className='!-mb-1 flex gap-x-1 font-normal'
                            >
                              Pinpoint location{' '}
                              <span className='text-muted-foreground'>( Optional )</span>
                            </Label>
                            <span className='text-muted-foreground'>Required for Gojek/Grab</span>
                          </div>
                          <button
                            type='button'
                            onClick={() => setIsShowMap(!isShowMap)}
                            className='flex h-8 items-center text-primary underline'
                          >
                            <MapPinIcon className='mr-2 h-4 w-4' />
                            pinpoint on map
                          </button>
                        </div>

                        {isShowMap && (
                          <MapService
                            onResultUpdate={handleSelectPinpoint}
                            coordinates={{
                              lat: pinpoint?.lat ? parseFloat(pinpoint.lat) : 0,
                              lng: pinpoint?.long ? parseFloat(pinpoint.long) : 0,
                            }}
                          />
                        )}

                        {!isEmpty(normalizePinpoint(pinpoint)) && (
                          <div className='2xl flex items-center gap-x-1 text-green-500'>
                            <p>Pinned</p>
                            <CircleCheck className='h-4 w-4' />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='border-b pb-6 pt-4'>
                      <FormControl>
                        <div>
                          <Label htmlFor='note' className='mb-1 flex gap-x-1 font-normal'>
                            Notes order <span className='text-muted-foreground'>( Optional )</span>
                          </Label>
                          <Textarea
                            id='notes'
                            name='notes'
                            value={values.notes}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder='Notes order'
                          />
                        </div>
                      </FormControl>
                    </div>

                    {!storeSetting?.is_digital_product && (
                      <div className='py-5'>
                        <CardShippingCheckout
                          carts={carts as HttpTypes.StoreCartDetail}
                          dataRates={
                            {
                              ...dataShipping,
                              hash,
                            } as BaseShippingRatesPayload
                          }
                          disabled={!areaData?.area_id}
                          hasNoPinpoint={
                            !pinpoint ||
                            (!pinpoint.lat && !pinpoint.long) ||
                            isEmpty(normalizePinpoint(pinpoint))
                          }
                          isLoading={isLoadingSelectShipping}
                          setIsCod={setIsCODAvailable}
                          handleChange={handleSelectShipping}
                          onInsuranceChange={handleOnCheckedInsurance}
                          defaultInsuranceChecked={isInsurance}
                          selectedOption={selectedOption}
                          storeSetting={storeSetting}
                          colorScheme={content.colorSchemeForm}
                        />
                      </div>
                    )}

                    {/* Mobile Checkout Item */}
                    <CheckoutItem
                      className='-mx-6 flex md:hidden'
                      content={content}
                      carts={carts as HttpTypes.StoreCartDetail}
                      isShippingInsurance={isInsurance as boolean}
                      isLoadingSelectShipping={isLoadingSelectShipping as boolean}
                      promotionTheme={promotionTheme}
                      selectedOption={selectedOption}
                    />
                    {isCODAvailable && (
                      <div className='pb-2 pt-6'>
                        <CardPaymentMethod
                          values={paymentMethod}
                          isLoading={isLoadingSelectShipping}
                          onChange={handleSelectPayment}
                          isCodAvailable={isCODAvailable}
                          isDropship={dataDropshipper.isDropshipper}
                        />
                      </div>
                    )}
                  </FormItem>
                </>
              ) : (
                <FormItem>
                  <div className='pb-2'>
                    <div className='mb-3 flex items-center'>
                      <div className='flex items-center gap-x-2'>
                        <MailIcon className='mt-0.5 h-5 w-5' />
                        <h2 className='text-lg md:text-xl'>Contact</h2>
                      </div>
                    </div>
                    <div className='mb-4 w-full space-y-4'>
                      <div className='flex w-full flex-col items-center gap-4 md:flex-row'>
                        <FormControl>
                          <div className='w-full flex-col'>
                            <Label
                              htmlFor='customer.first_name'
                              className='mb-1 font-normal'
                              aria-required
                            >
                              First name
                            </Label>
                            <Input
                              id='customer.first_name'
                              name='customer.first_name'
                              value={values.customer?.first_name}
                              onChange={(e) => {
                                handleChange(e);
                                setCustomer({
                                  ...customer,
                                  first_name: e.target.value,
                                });
                              }}
                              onBlur={handleBlur}
                              placeholder='First name'
                              required
                            />
                          </div>
                        </FormControl>
                        <FormControl>
                          <div className='w-full flex-col'>
                            <Label htmlFor='customer.last_name' className='mb-1 font-normal'>
                              Last name
                            </Label>
                            <Input
                              id='customer.last_name'
                              name='customer.last_name'
                              value={values.customer?.last_name}
                              className='w-full'
                              classNameWrapper='w-full'
                              onChange={(e) => {
                                handleChange(e);
                                setCustomer({
                                  ...customer,
                                  last_name: e.target.value,
                                });
                              }}
                              onBlur={handleBlur}
                              placeholder='Last name'
                            />
                          </div>
                        </FormControl>
                      </div>
                    </div>
                    <div className='mb-4 w-full space-y-4'>
                      <FormControl>
                        <div className='w-full flex-col'>
                          <Label htmlFor='customer.email' className='mb-1 font-normal'>
                            Email Address
                          </Label>
                          <Input
                            id='customer.email'
                            name='customer.email'
                            value={values.customer?.email}
                            className='w-full'
                            classNameWrapper='w-full'
                            onChange={(e) => {
                              handleChange(e);
                              setCustomer({
                                ...customer,
                                email: e.target.value,
                              });
                            }}
                            onBlur={handleBlur}
                            placeholder='Email'
                            required
                          />
                        </div>
                      </FormControl>
                    </div>
                    <div className='w-full space-y-4'>
                      <FormControl>
                        <div className='flex-col'>
                          <Label
                            htmlFor='customer.phone'
                            className='mb-1 font-normal'
                            aria-required
                          >
                            Phone number
                          </Label>
                          <Input
                            id='customer.phone'
                            name='customer.phone'
                            value={values.customer?.phone}
                            onChange={(e) => {
                              handleChange(e);
                              setCustomer({
                                ...customer,
                                phone: e.target.value,
                              });
                            }}
                            onBlur={handleBlur}
                            placeholder='Phone'
                            maxLength={20}
                            required
                          />
                        </div>
                      </FormControl>
                    </div>

                    <PickupAddress
                      selectedLocation={
                        selectedLocationPickup &&
                        'available_stock' in selectedLocationPickup &&
                        typeof selectedLocationPickup.available_stock === 'number'
                          ? (selectedLocationPickup as HttpTypes.StoreOrderLocationPickup)
                          : undefined
                      }
                      setSelectedLocation={setSelectedLocationPickup}
                      setDatePickup={setDatePickup}
                      colorScheme={content.colorSchemeForm}
                      orderSettings={storeSetting}
                      preview={isPreview as boolean}
                      theme={theme}
                      selectedOption={selectedOption}
                      carts={carts}
                    />

                    <div className='flex items-center justify-between pb-3 pt-6'>
                      <div className='flex items-center gap-x-2'>
                        <Calendar className='mt-0.5 h-5 w-5' />
                        <h2 className='tmd:text-xl text-lg'>Pickup Schedule</h2>
                      </div>
                    </div>
                    <div className='w-full flex-col'>
                      <Label htmlFor='pickup_date' className='mb-1 font-normal' aria-required>
                        Date and Time
                      </Label>
                      <PickupSchedule
                        date={datePickup}
                        setDate={setDatePickup}
                        selectedLocation={
                          selectedLocationPickup as HttpTypes.StoreOrderLocationPickup
                        }
                        preparationTime={storeSetting?.min_preparation_time || 0}
                      />
                    </div>
                  </div>
                </FormItem>
              )}
              <Button
                type='submit'
                variant='default'
                className={cn(
                  'flex w-full items-center rounded-lg py-6',
                  isEmpty(content.colorSchemeForm) && 'theme_button'
                )}
                disabled={disableButtonPay()}
                style={
                  !isEmpty(content.colorSchemeForm)
                    ? {
                        backgroundColor: content.colorSchemeForm?.btnSolidBackgroud,
                        color: content?.colorSchemeForm?.btnSolidlabel,
                      }
                    : {}
                }
              >
                {isSubmitted ? <LoadingAnimationIcon /> : content.labelButtonPayNow || 'Pay Now'}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
      <ModalSelectAddress
        openModal={modalSelectAddress.isShow}
        setOpenModal={modalSelectAddress.setIsShow}
        data={listAddress}
        colorScheme={content.colorSchemeForm}
        onSelect={handleChangeAddress}
      />
      <ModalAlert
        openModal={modalCODPayment.isShow}
        setOpenModal={modalCODPayment.setIsShow}
        onConfirm={handleSubmitCheckoutCOD}
        isLoading={isSubmitted}
        title='Confirm COD Payment'
        description={
          <div className='flex w-full flex-col gap-2 text-justify'>
            <span className='flex items-start'>
              Are you sure you want to choose Cash on Delivery (COD) as your payment method? Please
              note the following:
            </span>
            <ul className='flex flex-col gap-2'>
              <li className='flex gap-2'>
                <div className='mt-1.5 h-1.5 w-1.5 rounded-full bg-black' />
                <span className='flex-1'>
                  Ensure someone is available to receive and pay for the order at the delivery
                  address.
                </span>
              </li>
              <li className='flex gap-2'>
                <div className='mt-1.5 h-1.5 w-1.5 rounded-full bg-black' />
                <span className='flex-1'>
                  Failure to pay upon delivery may result in restrictions on future COD orders.
                </span>
              </li>
            </ul>
            <span>
              if you agree, click <b>Confirm</b> to proceed.
            </span>
          </div>
        }
      />
      <ModalAlert
        openModal={modalForcePromotion.isShow}
        setOpenModal={modalForcePromotion.setIsShow}
        buttonCancelText='No, Go Back'
        buttonConfirmText='Continue'
        onConfirm={handleForcePromotion}
        onCancel={
          cartTheme?.cartType !== 'drawer' ? () => router.push('/cart') : () => router.push('/')
        }
        title='Remove Invalid Promotions and Continue'
        description={
          <div className='flex w-full flex-col gap-2 text-justify'>
            <p>
              The following promotions are no longer valid or don&apos;t meet the requirements, and
              will be removed if you continue:
            </p>
            <ul className='list-disc pl-4 font-semibold'>
              {listPromoError &&
                listPromoError.length > 0 &&
                listPromoError.map((promo, index) => <li key={index}>{promo?.promotion_name}</li>)}
            </ul>
            <p>
              If you choose to continue, these promotions will be removed and you&apos;ll stay on
              the checkout page
            </p>
            {cartTheme?.cartType !== 'drawer' ? (
              <p>If not, you&apos;ll be redirected to your cart to review your items</p>
            ) : (
              <p>If not, you&apos;ll be redirected to the homepage to continue shopping</p>
            )}
          </div>
        }
      />

      <style jsx>
        {`
          .bg_checkout_form {
            background-color: ${content.colorSchemeForm?.background || 'white'};
            color: ${content.colorSchemeForm?.text || '#52535B'};
          }
          .button_color_scheme {
            color: ${content.colorSchemeForm?.btnSolidBackgroud || '#52535B'};
          }
          .button_color_scheme:hover {
            opacity: 0.5;
          }
        `}
      </style>
    </>
  );
};
